import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="bg-white border-b border-forest/10 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-forest hover:text-lime transition-colors">
            <ArrowLeft size={24} />
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="text-lime" size={24} />
            <h1 className="text-xl font-semibold text-forest">Política de Privacidade</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm p-6 md:p-8 space-y-6"
        >
          <p className="text-stone text-sm">Última atualização: Abril de 2026</p>

          {/* Introdução */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Sua Privacidade é Importante</h2>
            <p className="text-stone leading-relaxed">
              O Vocalira se compromete a proteger sua privacidade. Esta política explica 
              de forma simples quais dados coletamos, como usamos e como protegemos suas informações.
            </p>
          </section>

          {/* Dados Coletados */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Dados que Coletamos</h2>
            <ul className="text-stone leading-relaxed space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span><strong>Email:</strong> Para criar e identificar sua conta.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span><strong>Nome:</strong> Para personalizar sua experiência.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span><strong>Preferências de idioma:</strong> Idioma nativo e idioma que está aprendendo.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span><strong>Mensagens do chat:</strong> Para manter o histórico das suas conversas.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span><strong>Dados de uso:</strong> Estatísticas como mensagens enviadas e tempo de prática.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span><strong>Vocabulário salvo:</strong> Palavras que você escolher guardar para estudo.</span>
              </li>
            </ul>
          </section>

          {/* Uso dos Dados */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Como Usamos seus Dados</h2>
            <ul className="text-stone leading-relaxed space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Permitir o funcionamento do app e suas funcionalidades.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Personalizar as conversas de acordo com seu nível e objetivos.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Melhorar sua experiência de aprendizado.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Enviar comunicações importantes sobre sua conta.</span>
              </li>
            </ul>
          </section>

          {/* Armazenamento */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Armazenamento de Dados</h2>
            <p className="text-stone leading-relaxed">
              Seus dados são armazenados de forma segura em banco de dados MongoDB Atlas, 
              com criptografia e proteção por senha. Mantemos seus dados enquanto sua conta 
              estiver ativa.
            </p>
          </section>

          {/* Pagamentos */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Pagamentos</h2>
            <p className="text-stone leading-relaxed">
              Os pagamentos são processados pelo <strong>Stripe</strong>, uma plataforma segura 
              e reconhecida mundialmente. <strong>Não armazenamos</strong> dados de cartão de 
              crédito ou débito. Todas as transações financeiras são processadas diretamente 
              pelo Stripe.
            </p>
          </section>

          {/* Compartilhamento */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Compartilhamento de Dados</h2>
            <ul className="text-stone leading-relaxed space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span><strong>Não vendemos</strong> seus dados pessoais para terceiros.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Suas mensagens são enviadas para serviços de IA (OpenAI) apenas para gerar respostas.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Dados de pagamento são compartilhados apenas com o Stripe para processar transações.</span>
              </li>
            </ul>
          </section>

          {/* Segurança */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Segurança</h2>
            <p className="text-stone leading-relaxed">
              Utilizamos medidas de segurança para proteger seus dados, incluindo:
            </p>
            <ul className="text-stone leading-relaxed space-y-2 mt-2">
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Senhas criptografadas (nunca armazenamos sua senha em texto).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Conexões seguras (HTTPS).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Autenticação por token (JWT).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime mt-1">•</span>
                <span>Limites de requisições para prevenir abusos.</span>
              </li>
            </ul>
          </section>

          {/* Seus Direitos */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Seus Direitos</h2>
            <p className="text-stone leading-relaxed">
              Você pode solicitar a exclusão da sua conta e de todos os seus dados a qualquer 
              momento através das configurações do app ou entrando em contato conosco.
            </p>
          </section>

          {/* Alterações */}
          <section>
            <h2 className="text-lg font-semibold text-forest mb-3">Alterações nesta Política</h2>
            <p className="text-stone leading-relaxed">
              Podemos atualizar esta política ocasionalmente. Quando houver mudanças importantes, 
              você será notificado.
            </p>
          </section>

          {/* Contato */}
          <section className="pt-4 border-t border-forest/10">
            <p className="text-stone text-sm">
              Dúvidas sobre privacidade? Entre em contato conosco através do app ou por email.
            </p>
          </section>
        </motion.div>

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <Link to="/terms" className="text-forest hover:text-lime transition-colors text-sm">
            Ver Termos de Uso
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
